import { expose, transfer } from 'comlink';
import { IDBPObjectStore } from 'idb';
import { OPERATION } from '../../des.dto';
import { IdbIssueMedia, Issue } from '../../issue/issue.dto';
import { WarehouseSyncStepTaskContext } from '../executors';
import { WarehouseFilesStepsWorker } from './warehouse-files.steps.worker';
import { PictureSyncRiskFilter } from '@damofire/data-stas';
import { HttpUtils, ISSUE_MEDIAS_STORE, ISSUES_STORE, MEDIAS_COLLECTION, updateBatchFrom, WW_ISSUES_COLLECTION, logger } from '@damofire/data-commons';

function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binaryString = atob(base64.split(',')[1]);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

export class WarehouseIssuesMediasFilesStepsWorker extends WarehouseFilesStepsWorker<IdbIssueMedia> {
  // The main thread uploads each picture and deletes its ADD operation once uploaded (see the push step).
  protected override readonly deletesPushedAddOperations = false;

  constructor() {
    super(ISSUE_MEDIAS_STORE, 'issues-medias', 'ISSUES MEDIAS');
  }

  protected async clean(
    ctx: WarehouseSyncStepTaskContext,
    onProgress?: (progress: number) => void,
    itemsProcessedCallback?: (change: { data: IdbIssueMedia; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): Promise<void> {
    logger.debug('WarehouseIssuesMediasFilesStepsWorker::clean', { division: ctx.division, category: 'sync' });
    return this.deleteAllDataObsoleteFromStore<IdbIssueMedia>(ISSUE_MEDIAS_STORE, ctx, (dto) => dto.division._id, itemsProcessedCallback, onProgress);
  }

  protected async push(
    ctx: WarehouseSyncStepTaskContext,
    onProgress?: (progress: number) => void,
    itemsProcessedCallback?: (change: { data: IdbIssueMedia; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): Promise<void> {
    logger.debug('WarehouseIssuesMediasFilesStepsWorker::push', { division: ctx.division, category: 'sync' });
    return super.push(ctx, onProgress, itemsProcessedCallback);
  }

  // Prefer the AVIF variant of each picture when it exists (the server falls back to the stored format otherwise).
  protected override downloadParams(): Record<string, unknown> {
    return { avif: true };
  }

  protected stripMedia(im: IdbIssueMedia): IdbIssueMedia {
    const { bytes, thumbBytes, ...change } = im;
    return change;
  }

  protected filterChanges(allFromStore: IdbIssueMedia[]): string[] {
    return allFromStore.filter((cm: IdbIssueMedia) => !!cm.bytesPath).map((cm: IdbIssueMedia) => cm.filename);
  }

  protected override async filterMedias(allFromDivision: IdbIssueMedia[], ctx: WarehouseSyncStepTaskContext): Promise<IdbIssueMedia[]> {
    const { picturesRiskLevelFilter } = ctx;
    if (picturesRiskLevelFilter == null || picturesRiskLevelFilter.includes(PictureSyncRiskFilter.ALL)) {
      logger.info('Downloading all issues-medias files, no risk filter', { division: ctx.division, total: allFromDivision.length, category: 'sync' });
      return allFromDivision;
    }
    if (!picturesRiskLevelFilter.length) {
      logger.info('Downloading no issues-medias files, empty risk filter', { division: ctx.division, total: allFromDivision.length, category: 'sync' });
      return [];
    }

    const issues = await this.idbService.getAllFromIndex<Issue>(ISSUES_STORE, 'division', ctx.division);
    const issueMap = new Map(issues.map((i) => [i._id, i]));
    const selectedDescriptions = new Set(picturesRiskLevelFilter.map((f) => f.toUpperCase()));

    const kept: IdbIssueMedia[] = [];
    let missingIssue = 0;
    let riskMismatch = 0;
    for (const m of allFromDivision) {
      const desc = issueMap.get(m.issue?._id)?.riskLevel?.description?.toUpperCase();
      if (!desc) {
        missingIssue++;
        continue;
      }
      if (!selectedDescriptions.has(desc)) {
        riskMismatch++;
        continue;
      }
      kept.push(m);
    }

    logger.info('Filtered issues-medias files by risk level', {
      division: ctx.division,
      filter: [...selectedDescriptions],
      total: allFromDivision.length,
      kept: kept.length,
      droppedMissingIssue: missingIssue,
      droppedRiskMismatch: riskMismatch,
      category: 'sync',
    });

    return kept;
  }

  protected async addRemoteData(data: IdbIssueMedia[]): Promise<void> {
    logger.debug('Work is delegated to main thread', { category: 'sync' });
  }

  protected async deleteRemoteData(data: IdbIssueMedia[]): Promise<void> {
    const byWarehouse = data.reduce(
      (byWarehouse: Record<string, IdbIssueMedia[]>, issueMedia: IdbIssueMedia) => {
        if (!byWarehouse[issueMedia.division._id]) {
          byWarehouse[issueMedia.division._id] = [];
        }

        byWarehouse[issueMedia.division._id].push(issueMedia);
        return byWarehouse;
      },
      {} as Record<string, IdbIssueMedia[]>,
    );

    for (const warehouse of Object.keys(byWarehouse)) {
      for (const im of byWarehouse[warehouse]) {
        try {
          await HttpUtils.post<void>(this.environment.appConfig.fns, 'ww-issueMediaDelete', {
            data: {
              issueId: im.issue._id,
              divisionId: warehouse,
              _id: im._id,
            },
          });
        } catch (e) {
          logger.error('Could not perform issue media delete', { error: e, category: 'sync' });
        }
      }
    }
  }

  protected persist(item: IdbIssueMedia, objectStore: IDBPObjectStore<unknown, [string], string, IDBTransactionMode>): IdbIssueMedia {
    if (item.bytes) {
      item.bytesPath = `/${item.division._id}/medias/${item.web}`;
      item.thumbBytesPath = item.thumbBytes ? `/${item.division._id}/medias/${item.thumb}` : null;
      item.format = `image/${item.bytes.split(';')[0].split('/')[1]}`;
      const toPersist = this.stripMedia(item);
      // Rejection surfaces via tx.done; settled here to avoid unhandled rejection noise.
      // Promise.resolve wrapper: the persist spec mocks put with a bare spy returning undefined.
      void Promise.resolve(objectStore.put(toPersist)).catch((): undefined => undefined);
      return toPersist;
    }

    return null;
  }

  protected override sendToMainThread(
    persisted: IdbIssueMedia,
    itemsProcessedCallback: (change: { data: IdbIssueMedia; operation: OPERATION }, buffer: ArrayBuffer) => void,
  ): void {
    if (persisted.bytes) {
      const imageBuffer: ArrayBuffer = base64ToArrayBuffer(persisted.bytes);
      itemsProcessedCallback({ data: { ...persisted, thumbBytesPath: null, thumbBytes: null, bytes: null }, operation: OPERATION.ADD }, transfer(imageBuffer, [imageBuffer]));
    }

    if (persisted.thumbBytes) {
      const thumbBuffer: ArrayBuffer = base64ToArrayBuffer(persisted.thumbBytes);
      itemsProcessedCallback({ data: { ...persisted, bytesPath: null, bytes: null, thumbBytes: null }, operation: OPERATION.ADD }, transfer(thumbBuffer, [thumbBuffer]));
    }
  }

  protected async updateRemoteData(data: IdbIssueMedia[]): Promise<void> {
    const withAnnotations = data.filter((m: IdbIssueMedia) => (m.annotations as Array<unknown>)?.length);
    const byWarehouse = withAnnotations.reduce(
      (byWarehouse: Record<string, IdbIssueMedia[]>, im: IdbIssueMedia) => {
        const divisionId = im.division._id;
        if (!byWarehouse[divisionId]) {
          byWarehouse[divisionId] = [];
        }

        const { _id, issue, annotations, audit } = im;
        byWarehouse[divisionId].push({ _id, issue, annotations, audit });
        return byWarehouse;
      },
      {} as Record<string, IdbIssueMedia[]>,
    );

    for (const warehouse of Object.keys(byWarehouse)) {
      try {
        await updateBatchFrom(byWarehouse[warehouse], (d: IdbIssueMedia) => `${WW_ISSUES_COLLECTION}/${d.issue._id}/${MEDIAS_COLLECTION}/${d._id}`);
      } catch (err) {
        logger.error('Cannot update on remote', { error: err, category: 'sync' });
      }
    }
  }
}

expose(WarehouseIssuesMediasFilesStepsWorker);
